import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarPlus, Trash2, Clock3 } from "lucide-react";

function pad(n) { return String(n).padStart(2, "0"); }

export default function OfferedSlots() {
  const [items, setItems] = useState([]);
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/therapist/me/offered-slots");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load offered slots");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const add = async () => {
    if (!date || !time) {
      toast.error("Pick both a date and a time.");
      return;
    }
    const [hh, mm] = time.split(":").map(Number);
    const slot = new Date(date);
    slot.setHours(hh, mm, 0, 0);
    if (slot.getTime() <= Date.now()) {
      toast.error("Pick a time in the future.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/therapist/me/offered-slots", { slot: slot.toISOString() });
      toast.success("Time added — clients will see it on your booking page.");
      setTime("");
      // intentionally keep `date` selected so the counselor can add another time on the same day
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/therapist/me/offered-slots/${id}`);
      toast.success("Removed");
      setConfirmingId(null);
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove");
    }
  };

  // Quick-add common evening times
  const quickTimes = ["18:00", "18:30", "19:00", "19:30", "20:00", "20:30", "21:00"];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="offered-slots-panel">
      <div className="lg:col-span-7">
        <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-3">
          <span className="editorial-rule" />Offered times
        </div>
        <h2 className="font-serif text-3xl text-mindora-ink leading-tight">
          Open up specific times,<br />
          <em className="italic text-mindora-red">on top of your regular schedule.</em>
        </h2>
        <p className="text-sm text-mindora-muted mt-3 max-w-xl leading-relaxed">
          Clients usually pick from the grid generated by your Settings. Use this panel to
          open extra times — late evenings, weekends, one-off windows — that you'd be happy
          to take a session at. They'll appear alongside your usual slots on your booking page.
        </p>

        <div className="mt-8 rounded-2xl border border-mindora-line bg-mindora-panel p-5" data-testid="offered-slots-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-wider text-mindora-ink/80">Date</label>
              <div className="mt-2 rounded-2xl border border-mindora-line bg-mindora-bg p-3 inline-block">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => { if (d) setDate(d); }}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  data-testid="offered-slots-calendar"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-mindora-ink/80">Time (your local time)</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="offered-slots-time-input"
                className="mt-2 w-full rounded-md border border-mindora-line bg-mindora-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mindora-red"
              />
              <div className="mt-3">
                <div className="text-[11px] uppercase tracking-wider text-mindora-muted mb-2">Quick pick</div>
                <div className="flex flex-wrap gap-2">
                  {quickTimes.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTime(t)}
                      data-testid={`offered-slots-quick-${t}`}
                      className={`text-xs rounded-full border px-3 py-1 transition-colors ${
                        time === t
                          ? "bg-mindora-red text-mindora-bg border-mindora-red"
                          : "border-mindora-line text-mindora-ink/70 hover:border-mindora-red hover:text-mindora-red"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <Button
                onClick={add}
                disabled={busy || !date || !time}
                data-testid="offered-slots-add"
                className="mt-5 w-full btn-primary rounded-full h-11"
              >
                <CalendarPlus size={14} className="mr-2" />
                {busy ? "Adding…" : "Add this time"}
              </Button>
              <p className="text-[11px] text-mindora-muted mt-2 italic">
                Saved as your local time. Clients see it in their own timezone.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-5">
        <div className="text-xs uppercase tracking-[0.2em] text-mindora-ink/80 mb-3">
          Your offered times ({items.length})
        </div>
        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-mindora-line bg-mindora-bg p-6 text-sm text-mindora-muted">
            No extra times offered yet. Add one on the left — it'll show up here instantly.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="offered-slots-list">
            {items.map((it) => {
              const d = new Date(it.slot);
              return (
                <li
                  key={it.id}
                  className="flex items-center justify-between rounded-xl border border-mindora-line bg-mindora-panel px-4 py-3"
                  data-testid={`offered-slot-${it.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Clock3 size={14} className="text-mindora-red" />
                    <div>
                      <div className="font-serif text-base text-mindora-ink">
                        {format(d, "EEEE, MMMM do")}
                      </div>
                      <div className="text-xs text-mindora-muted">
                        {pad(d.getHours())}:{pad(d.getMinutes())} (your local)
                      </div>
                    </div>
                  </div>
                  {confirmingId === it.id ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => remove(it.id)}
                        data-testid={`offered-slot-${it.id}-confirm`}
                        className="text-xs uppercase tracking-wider rounded-full bg-mindora-red text-mindora-bg px-3 py-1 hover:opacity-90"
                      >
                        Remove
                      </button>
                      <button
                        onClick={() => setConfirmingId(null)}
                        data-testid={`offered-slot-${it.id}-cancel`}
                        className="text-xs uppercase tracking-wider text-mindora-muted hover:text-mindora-ink"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmingId(it.id)}
                      data-testid={`offered-slot-${it.id}-remove`}
                      className="text-mindora-muted hover:text-mindora-red transition-colors"
                      aria-label="Remove"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
